/**
 * Tiny fetch wrapper for our cookie-auth API.
 *
 * - Always sends the session cookie (`credentials: "same-origin"`)
 * - Treats `application/json` responses uniformly
 * - Surfaces server-side error details (`{ detail: ... }`) as a typed error
 */

export class ApiError extends Error {
  status: number;
  detail: unknown;
  reauthRequired: boolean;

  constructor(status: number, message: string, detail: unknown, reauthRequired = false) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.reauthRequired = reauthRequired;
  }
}

interface RequestOpts {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

// FastAPI/Pydantic 422 errors return `detail` as an array of objects shaped
// like `{ loc: ["body", "field"], msg: "…", type: "…" }`. Surface the first
// one in human-readable form so the caller's `.message` is actually useful;
// the original structured detail is still available on `ApiError.detail` for
// callers that want to do something fancier.
function humanizeErrorDetail(detail: unknown, res: Response): string {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (first && typeof first === "object") {
      const loc = (first as { loc?: unknown }).loc;
      const msg = (first as { msg?: unknown }).msg;
      if (typeof msg === "string") {
        if (Array.isArray(loc) && loc.length > 0) {
          // Skip the leading "body"/"query"/"path" segment; the field
          // name is what the user cares about.
          const path = loc
            .slice(loc[0] === "body" || loc[0] === "query" || loc[0] === "path" ? 1 : 0)
            .join(".");
          return path ? `${path}: ${msg}` : msg;
        }
        return msg;
      }
    }
  }
  if (detail && typeof detail === "object") {
    const m = (detail as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return `Request failed (${res.status} ${res.statusText})`;
}

export async function api<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { method = "GET", body, signal } = opts;

  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const init: RequestInit = {
    method,
    headers,
    credentials: "same-origin",
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  if (signal) init.signal = signal;

  const res = await fetch(path, init);

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const detail =
      parsed && typeof parsed === "object" && parsed !== null && "detail" in parsed
        ? (parsed as { detail: unknown }).detail
        : parsed;
    const message = humanizeErrorDetail(detail, res);
    const reauth = res.headers.get("X-Reauth-Required") === "1";
    throw new ApiError(res.status, message, detail, reauth);
  }

  return parsed as T;
}

// ── Bulk Excel import (multipart) ────────────────────────────────────────────

export interface ImportRowError {
  row: number;
  message: string;
  column?: string | null;
  value?: string | null;
  accepted?: string[] | null;
}

export interface ImportSummary {
  entity: string;
  total_rows: number;
  created: number;
  skipped: number;
  errors: ImportRowError[];
}

/**
 * Upload an .xlsx to a bulk-import endpoint. Uses raw fetch (not `api`)
 * because the body is multipart/form-data — the browser must set the
 * Content-Type boundary itself, so we do NOT send a JSON content type.
 */
export async function importXlsx(path: string, file: File): Promise<ImportSummary> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(path, {
    method: "POST",
    body: form,
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  if (!res.ok) {
    const detail =
      parsed && typeof parsed === "object" && parsed !== null && "detail" in parsed
        ? (parsed as { detail: unknown }).detail
        : parsed;
    const message = typeof detail === "string" ? detail : `Import failed (${res.status}).`;
    throw new ApiError(res.status, message, detail);
  }
  return parsed as ImportSummary;
}

export type Role = "admin" | "manager" | "production" | "sales";

export interface UserPublic {
  id: string;
  email: string;
  name: string | null;
  role: Role;
  custom_role_id?: string | null;
  is_active: boolean;
  is_owner: boolean;
  email_verified_at: string | null;
  last_login_at: string | null;
}

export interface MeResponse {
  user: UserPublic;
  custom_role_name?: string | null;
  effective_permissions?: string[];
  session_expires_at: string;
  sensitive_op_active: boolean;
}

export const authApi = {
  me: () => api<MeResponse>("/api/auth/me"),
  login: (email: string, password: string) =>
    api<MeResponse>("/api/auth/login", { method: "POST", body: { email, password } }),
  logout: () => api<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  acceptInvite: (token: string, password: string, name?: string) =>
    api<MeResponse>("/api/auth/accept-invite", {
      method: "POST",
      body: { token, password, name },
    }),
  verifyOnboarding: (email: string, otp: string, password: string, name?: string) =>
    api<MeResponse>("/api/auth/onboarding/verify", {
      method: "POST",
      body: { email, otp, password, name },
    }),
  requestPasswordReset: (email: string) =>
    api<{ message: string }>("/api/auth/password-reset/request", {
      method: "POST",
      body: { email },
    }),
  confirmPasswordReset: (token: string, new_password: string) =>
    api<{ message: string }>("/api/auth/password-reset/confirm", {
      method: "POST",
      body: { token, new_password },
    }),
};

export interface SetupStatus {
  needs_setup: boolean;
  setup_token_configured: boolean;
  smtp_configured: boolean;
  google_configured: boolean;
  public_url: string;
  drive_callback_uri: string;
  login_callback_uri: string;
}

export interface SetupConfiguration {
  setup_token: string;
  org_name: string;
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  smtp_password: string;
  smtp_from: string;
  test_email: string;
  google_oauth_client_id: string;
  google_oauth_client_secret: string;
}

export const setupApi = {
  status: () => api<SetupStatus>("/api/setup/status"),
  configure: (body: SetupConfiguration) =>
    api<{ message: string }>("/api/setup/configure", { method: "POST", body }),
  requestOwnerCode: (setup_token: string, email: string, name: string) =>
    api<{ message: string }>("/api/setup/owner-code", {
      method: "POST",
      body: { setup_token, email, name },
    }),
};

export type CustomFieldType =
  | "short_text"
  | "rich_text"
  | "number"
  | "date"
  | "boolean"
  | "picklist";

export interface CustomFieldDefinition {
  id: string;
  entity_type: string;
  key: string;
  label: string;
  field_type: CustomFieldType;
  help_text: string | null;
  picklist_options: string[];
  include_in_import: boolean;
  include_in_export: boolean;
  is_active: boolean;
  position: number;
  created_at: string;
  updated_at: string;
}

export const customFieldsApi = {
  definitions: (entity: string, includeInactive = false) =>
    api<{ items: CustomFieldDefinition[] }>(
      `/api/custom-fields/definitions?entity_type=${encodeURIComponent(entity)}&include_inactive=${includeInactive}`,
    ),
  create: (body: {
    entity_type: string;
    key: string;
    label: string;
    field_type: CustomFieldType;
    help_text?: string | null;
    picklist_options?: string[];
    include_in_import: boolean;
    include_in_export: boolean;
    position?: number;
  }) => api<CustomFieldDefinition>("/api/custom-fields/definitions", { method: "POST", body }),
  update: (
    id: string,
    body: Partial<
      Pick<
        CustomFieldDefinition,
        | "label"
        | "help_text"
        | "picklist_options"
        | "include_in_import"
        | "include_in_export"
        | "is_active"
        | "position"
      >
    >,
  ) => api<CustomFieldDefinition>(`/api/custom-fields/definitions/${id}`, { method: "PATCH", body }),
  values: (entity: string, recordKey: string) =>
    api<{ entity_type: string; record_key: string; values: Record<string, unknown> }>(
      `/api/custom-fields/values/${encodeURIComponent(entity)}/${encodeURIComponent(recordKey)}`,
    ),
  saveValues: (entity: string, recordKey: string, values: Record<string, unknown>) =>
    api<{ entity_type: string; record_key: string; values: Record<string, unknown> }>(
      `/api/custom-fields/values/${encodeURIComponent(entity)}/${encodeURIComponent(recordKey)}`,
      { method: "PUT", body: { values } },
    ),
};

export interface GoogleDriveStatus {
  configured: boolean;
  connected: boolean;
  connected_email: string | null;
  folder_name: string | null;
  folder_url: string | null;
  health: string | null;
  last_error: string | null;
  connected_at: string | null;
  last_tested_at: string | null;
}

export interface GoogleDrivePermission {
  id: string;
  type: string;
  role: string;
  email: string | null;
  display_name: string | null;
  is_owner: boolean;
  is_public: boolean;
}

export const googleDriveApi = {
  status: () => api<GoogleDriveStatus>("/api/integrations/google/status"),
  connect: () =>
    api<{ authorization_url: string }>("/api/integrations/google/connect", {
      method: "POST",
    }),
  test: () =>
    api<{ ok: boolean; folder_name: string }>("/api/integrations/google/test", {
      method: "POST",
    }),
  collaborators: () =>
    api<{ items: GoogleDrivePermission[] }>("/api/integrations/google/collaborators"),
  addCollaborator: (email: string, role: "reader" | "writer") =>
    api<GoogleDrivePermission>("/api/integrations/google/collaborators", {
      method: "POST",
      body: { email, role },
    }),
  updateCollaborator: (id: string, role: "reader" | "writer") =>
    api<GoogleDrivePermission>(`/api/integrations/google/collaborators/${id}`, {
      method: "PATCH",
      body: { role },
    }),
  removeCollaborator: (id: string) =>
    api<undefined>(`/api/integrations/google/collaborators/${id}`, {
      method: "DELETE",
    }),
};

// ── Contacts ────────────────────────────────────────────────────────────────

export interface Contact {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  address: string | null;
  account_id: string | null;
  assigned_to_id: string | null;
  assigned_to_name: string | null;
  created_by_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContactList {
  items: Contact[];
  total: number;
  limit: number;
  offset: number;
}

export interface ContactCreate {
  name: string;
  phone: string;
  email?: string | null;
  address?: string | null;
  account_id?: string | null;
}

/**
 * Partial update payload (PRD §10.5).
 *
 * The server uses Pydantic's `model_fields_set` to distinguish "field absent"
 * (leave column alone) from "field set to null" (clear the column). To
 * preserve that distinction over JSON we ONLY include the keys the caller
 * actually wants to send; never send `undefined`.
 *
 * Helper: `buildContactUpdate(...)` below assembles the right shape.
 */
export interface ContactUpdate {
  name?: string;
  phone?: string;
  email?: string | null;
  address?: string | null;
  account_id?: string | null;
  assigned_to_id?: string | null;
}

export interface TrashedContact extends Contact {
  deleted_at: string;
  deleted_by_id: string | null;
}

export interface TrashedContactList {
  items: TrashedContact[];
  total: number;
  limit: number;
  offset: number;
}

/** PRD §10.1 — server returns ONE of these as the `detail` of a 409 response. */
export type DuplicatePhoneDetail =
  | {
      code: "duplicate_phone_visible";
      message: string;
      existing_contact_id: string;
      phone: string;
    }
  | { code: "duplicate_phone_hidden"; message: string; phone: string };

export function isDuplicatePhoneDetail(d: unknown): d is DuplicatePhoneDetail {
  return (
    typeof d === "object" &&
    d !== null &&
    "code" in d &&
    (d.code === "duplicate_phone_visible" || d.code === "duplicate_phone_hidden")
  );
}

export const contactsApi = {
  list: (
    params: {
      limit?: number | undefined;
      offset?: number | undefined;
      q?: string | undefined;
      assigned_to_id?: string | undefined;
      account_id?: string | undefined;
      unassigned_account?: boolean | undefined;
      /** AND of every tag id; pass an empty array or undefined to skip. */
      tag_ids?: string[] | undefined;
    } = {},
  ) => {
    const search = new URLSearchParams();
    if (params.limit !== undefined) search.set("limit", String(params.limit));
    if (params.offset !== undefined) search.set("offset", String(params.offset));
    if (params.q) search.set("q", params.q);
    if (params.assigned_to_id) search.set("assigned_to_id", params.assigned_to_id);
    if (params.account_id) search.set("account_id", params.account_id);
    if (params.unassigned_account)
      search.set("unassigned_account", String(params.unassigned_account));
    if (params.tag_ids && params.tag_ids.length > 0) {
      for (const tid of params.tag_ids) search.append("tag_id", tid);
    }
    const qs = search.toString();
    return api<ContactList>(`/api/contacts${qs ? `?${qs}` : ""}`);
  },
  get: (id: string) => api<Contact>(`/api/contacts/${id}`),
  create: (body: ContactCreate) => api<Contact>("/api/contacts", { method: "POST", body }),
  update: (id: string, body: ContactUpdate) =>
    api<Contact>(`/api/contacts/${id}`, { method: "PATCH", body }),
  remove: (id: string) => api<undefined>(`/api/contacts/${id}`, { method: "DELETE" }),
  restore: (id: string) => api<Contact>(`/api/contacts/${id}/restore`, { method: "POST" }),
  listTrash: (params: { limit?: number; offset?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.limit !== undefined) search.set("limit", String(params.limit));
    if (params.offset !== undefined) search.set("offset", String(params.offset));
    const qs = search.toString();
    return api<TrashedContactList>(`/api/contacts/trash/list${qs ? `?${qs}` : ""}`);
  },
};

// ── Accounts ────────────────────────────────────────────────────────────────

export interface Account {
  id: string;
  name: string;
  address: string | null;
  is_public: boolean;
  is_active: boolean;
  owner_id: string | null;
  merged_into_id: string | null;
  created_by_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface AccountList {
  items: Account[];
  total: number;
  limit: number;
  offset: number;
}

export interface AccountCreate {
  name: string;
  is_public?: boolean;
  owner_id?: string | null;
  address?: string | null;
}

/**
 * Partial update payload (PRD §10.4 + §10.5).
 *
 * Same `model_fields_set` semantics as ContactUpdate — only send keys you
 * actually want the server to apply.
 */
export interface AccountUpdate {
  name?: string;
  is_public?: boolean;
  owner_id?: string | null;
  address?: string | null;
}

export interface TrashedAccount extends Account {
  deleted_at: string;
  deleted_by_id: string | null;
}

export interface TrashedAccountList {
  items: TrashedAccount[];
  total: number;
  limit: number;
  offset: number;
}

export interface AccountAccessGrant {
  user_id: string;
  granted_by_id: string | null;
  granted_at: string;
}

export interface AccountAccessGrantList {
  items: AccountAccessGrant[];
}

export interface AccountPipelineStageBucket {
  /** Picklist-driven (Apr-2026 addendum): backend buckets every active
   *  picklist stage. Seed values listed in ``DEAL_STAGES`` plus any
   *  manager-added ones. */
  stage: string;
  count: number;
}

export interface AccountPipeline {
  account_id: string;
  open: number;
  won: number;
  lost: number;
  total: number;
  by_stage: AccountPipelineStageBucket[];
}

export const accountsApi = {
  list: (
    params: {
      limit?: number | undefined;
      offset?: number | undefined;
      q?: string | undefined;
      owner_id?: string | undefined;
      visibility?: "public" | "private" | undefined;
      unowned?: boolean | undefined;
      /** AND of every tag id; pass an empty array or undefined to skip. */
      tag_ids?: string[] | undefined;
    } = {},
  ) => {
    const search = new URLSearchParams();
    if (params.limit !== undefined) search.set("limit", String(params.limit));
    if (params.offset !== undefined) search.set("offset", String(params.offset));
    if (params.q) search.set("q", params.q);
    if (params.owner_id) search.set("owner_id", params.owner_id);
    if (params.visibility) search.set("visibility", params.visibility);
    if (params.unowned) search.set("unowned", String(params.unowned));
    if (params.tag_ids && params.tag_ids.length > 0) {
      for (const tid of params.tag_ids) search.append("tag_id", tid);
    }
    const qs = search.toString();
    return api<AccountList>(`/api/accounts${qs ? `?${qs}` : ""}`);
  },
  pipeline: (id: string) => api<AccountPipeline>(`/api/accounts/${id}/pipeline`),
  get: (id: string) => api<Account>(`/api/accounts/${id}`),
  create: (body: AccountCreate) => api<Account>("/api/accounts", { method: "POST", body }),
  update: (id: string, body: AccountUpdate) =>
    api<Account>(`/api/accounts/${id}`, { method: "PATCH", body }),
  remove: (id: string) => api<undefined>(`/api/accounts/${id}`, { method: "DELETE" }),
  restore: (id: string) => api<Account>(`/api/accounts/${id}/restore`, { method: "POST" }),
  listTrash: (params: { limit?: number; offset?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.limit !== undefined) search.set("limit", String(params.limit));
    if (params.offset !== undefined) search.set("offset", String(params.offset));
    const qs = search.toString();
    return api<TrashedAccountList>(`/api/accounts/trash/list${qs ? `?${qs}` : ""}`);
  },
  listGrants: (id: string) => api<AccountAccessGrantList>(`/api/accounts/${id}/grants`),
  addGrant: (id: string, user_id: string) =>
    api<AccountAccessGrant>(`/api/accounts/${id}/grants`, {
      method: "POST",
      body: { user_id },
    }),
  removeGrant: (id: string, user_id: string) =>
    api<undefined>(`/api/accounts/${id}/grants/${user_id}`, { method: "DELETE" }),
  listContactIds: (id: string) => api<string[]>(`/api/accounts/${id}/contacts`),
};

// ── Users (manager+ only — used by picker UIs) ──────────────────────────────

export interface UserListItem {
  id: string;
  email: string;
  name: string | null;
  role: Role;
  custom_role_id?: string | null;
  is_active: boolean;
  is_owner: boolean;
  email_verified_at: string | null;
  invite_accepted_at: string | null;
  last_login_at: string | null;
}

export interface UserRead extends UserListItem {
  invited_by_id: string | null;
  invited_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserListResponse {
  items: UserListItem[];
}

export const usersApi = {
  list: (params: { role?: Role; active_only?: boolean } = {}) => {
    const search = new URLSearchParams();
    if (params.role !== undefined) search.set("role", params.role);
    if (params.active_only !== undefined) search.set("active_only", String(params.active_only));
    const qs = search.toString();
    return api<UserListResponse>(`/api/users${qs ? `?${qs}` : ""}`);
  },
  create: (body: {
    email: string;
    name?: string | null;
    role: Role;
    custom_role_id?: string | null;
  }) => api<UserRead>("/api/users", { method: "POST", body }),
  update: (
    id: string,
    body: { name?: string | null; role?: Role; custom_role_id?: string | null },
  ) => api<UserRead>(`/api/users/${id}`, { method: "PATCH", body }),
  deactivate: (id: string) => api<UserRead>(`/api/users/${id}/deactivate`, { method: "POST" }),
  reactivate: (id: string) => api<UserRead>(`/api/users/${id}/reactivate`, { method: "POST" }),
  resendInvite: (id: string) => api<UserRead>(`/api/users/${id}/resend-invite`, { method: "POST" }),
  transferOwnership: (id: string) =>
    api<UserRead>(`/api/users/${id}/transfer-ownership`, { method: "POST" }),
};

export interface ActiveSession {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string | null;
  created_at: string;
  expires_at: string;
  last_seen_at: string;
  revoked_at: string | null;
  ip: string | null;
  user_agent: string | null;
  is_current: boolean;
}

export const securityApi = {
  listSessions: (activeOnly = true) =>
    api<{ items: ActiveSession[] }>(`/api/security/sessions?active_only=${activeOnly}`),
  revokeSession: (id: string) =>
    api<undefined>(`/api/security/sessions/${id}/revoke`, { method: "POST" }),
};

export interface ExportProfile {
  enabled: boolean;
  schedule_hour: number;
  schedule_minute: number;
  entities: string[];
  status_filters: Record<string, string[]>;
  optional_columns: Record<string, string[]>;
  drive_upload_enabled: boolean;
  local_retention_days: number;
  updated_at: string;
}

export interface ExportArtifact {
  id: string;
  entity: string;
  filename: string;
  status: string;
  row_count: number;
  sha256: string | null;
  drive_file_id: string | null;
  attempts: number;
  error: string | null;
  created_at: string;
  uploaded_at: string | null;
}

export interface ExportRun {
  id: string;
  trigger: string;
  status: string;
  scheduled_for: string | null;
  started_at: string;
  completed_at: string | null;
  requested_by_id: string | null;
  retry_of_id: string | null;
  error: string | null;
  artifacts: ExportArtifact[];
}

export const exportsApi = {
  profile: () => api<ExportProfile>("/api/exports/profile"),
  updateProfile: (body: Partial<ExportProfile>) =>
    api<ExportProfile>("/api/exports/profile", { method: "PATCH", body }),
  runs: () => api<{ items: ExportRun[] }>("/api/exports/runs"),
  runNow: () => api<ExportRun>("/api/exports/run-now", { method: "POST" }),
  retry: (id: string) => api<ExportRun>(`/api/exports/runs/${id}/retry`, { method: "POST" }),
  downloadUrl: (artifactId: string) => `/api/exports/artifacts/${artifactId}/download`,
};

export interface CustomRole {
  id: string;
  name: string;
  description: string | null;
  base_role: Exclude<Role, "admin">;
  permissions: string[];
  is_active: boolean;
  assigned_users: number;
  created_at: string;
  updated_at: string;
}

export interface PermissionCatalogue {
  items: Array<{ code: string; label: string }>;
  allowed_by_base_role: Record<string, string[]>;
}

export const customRolesApi = {
  list: () => api<{ items: CustomRole[] }>("/api/custom-roles"),
  permissions: () => api<PermissionCatalogue>("/api/custom-roles/permissions"),
  create: (body: {
    name: string;
    description?: string | null;
    base_role: Exclude<Role, "admin">;
    permissions: string[];
  }) => api<CustomRole>("/api/custom-roles", { method: "POST", body }),
  update: (
    id: string,
    body: Partial<{
      name: string;
      description: string | null;
      base_role: Exclude<Role, "admin">;
      permissions: string[];
      is_active: boolean;
    }>,
  ) => api<CustomRole>(`/api/custom-roles/${id}`, { method: "PATCH", body }),
};

// ── Deals (PRD §8 + §9.5 + §10.2) ───────────────────────────────────────────

// Seed values that ship with every deployment. Manager-added picklist
// options are *also* legal at runtime — any code that needs the live
// list reads it from the picklists API (see ``picklistsApi`` below).
// These constants stay so existing comparisons like ``stage === "won"``
// keep working with full type narrowing.
export const DEAL_STAGES = [
  "new",
  "contacted",
  "sample_shared",
  "negotiation",
  "won",
  "lost",
] as const;
/**
 * Built-in stage values. Note: with the picklist addendum (Apr-2026),
 * ``Deal.stage`` is typed as a plain ``string`` so manager-added stages
 * also fit. Use this union when *narrowing* against the seed set
 * (e.g. ``if (s === "won")``); use ``string`` when assigning.
 */
export type DealStage = (typeof DEAL_STAGES)[number];

export const DEAL_STATUSES = [
  "urgent",
  "active",
  "follow_up",
  "waiting",
  "cold",
  "inactive",
  "closed",
  "never",
] as const;
export type DealStatus = (typeof DEAL_STATUSES)[number];

// ``closed`` and ``never`` are *terminal* statuses: setting one on an
// open deal causes the backend to auto-close it (sets ``closed_at``).
// Useful for hiding terminal options from quick-action UIs that should
// only let the user mark the deal "in progress".
export const TERMINAL_STATUSES: readonly string[] = ["closed", "never"];
export function isTerminalStatus(s: string): boolean {
  return TERMINAL_STATUSES.includes(s);
}

export const DEAL_VALUES = ["high", "medium", "low"] as const;
export type DealValue = (typeof DEAL_VALUES)[number];

export const TERMINAL_STAGES: readonly string[] = ["won", "lost"];

export function isTerminalStage(s: string): boolean {
  return TERMINAL_STAGES.includes(s);
}

/**
 * Compact contact reference embedded on deal list rows so the table
 * can render a real name (and phone — what reps recognise their
 * contacts by) without N+1 round-trips. Single-item Deal reads leave
 * this null because the detail page already has the full contact
 * context.
 */
export interface DealContactSummary {
  id: string;
  name: string;
  phone: string | null;
}

export interface DealAccountSummary {
  id: string;
  name: string;
}

export interface Deal {
  id: string;
  /** Either contact_id or account_id (or both) is always set; backend
   *  CHECK ``ck_deals_has_parent`` enforces the floor. */
  contact_id: string | null;
  account_id: string | null;
  /**
   * Populated on list responses only. Null on single-item reads (the
   * detail page already has the parent) and when the parent has been
   * soft-deleted.
   */
  contact_summary: DealContactSummary | null;
  account_summary: DealAccountSummary | null;
  /**
   * Picklist-driven (Apr-2026 addendum): seed values are listed in
   * ``DEAL_STAGES`` but managers can add more, so the field is a plain
   * string at the type level. Use ``DealStage`` for *narrowing* (e.g.
   * ``s === "won"``) — it comparing fine because StrEnums.
   */
  stage: string;
  status: string;
  value: string;
  requirements: string | null;
  notes: string | null;
  assigned_to_id: string | null;
  assigned_to_name: string | null;
  created_by_id: string | null;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  next_follow_up_at: string | null;
}

export interface DealList {
  items: Deal[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Create a deal with a contact, an account, or both.
 *
 * v1.2: at least one of ``contact_id`` / ``account_id`` is required;
 * both is allowed and is the common case ("this person at this
 * company"). When both are set, the contact's own account_id MUST
 * equal ``account_id`` — the server returns 422
 * ``contact_account_mismatch`` otherwise.
 */
export interface DealCreate {
  contact_id?: string | null;
  account_id?: string | null;
  value?: string;
  status?: string;
  requirements?: string | null;
  notes?: string | null;
  assigned_to_id?: string | null;
}

/**
 * Partial update payload (PRD §10.5).
 *
 * Same ``model_fields_set`` semantics as ContactUpdate / AccountUpdate.
 * Setting ``stage`` to a terminal value (``won`` / ``lost``) closes the
 * deal; setting it back to a non-terminal value on a closed deal
 * triggers §8.6 reopen. (The v1.1 "contact must have an account before
 * closing" gate was dropped in v1.2.)
 *
 * ``contact_id`` is intentionally NOT updateable here — changing the
 * deal's primary parent is a destructive identity change. ``account_id``
 * IS updateable but the server enforces "contact-must-belong-to-account"
 * when both are set, returning 422 ``contact_account_mismatch`` on
 * mismatch.
 */
export interface DealUpdate {
  account_id?: string | null;
  stage?: string;
  status?: string;
  value?: string;
  requirements?: string | null;
  notes?: string | null;
  assigned_to_id?: string | null;
}

export interface TrashedDeal extends Deal {
  deleted_at: string;
  deleted_by_id: string | null;
}

export interface TrashedDealList {
  items: TrashedDeal[];
  total: number;
  limit: number;
  offset: number;
}

/** PRD §10.2 / §8.5 — server returns one of these as `detail` of a 409.
 *
 * v1.2: the existing-deal codes are now WARNINGS (overridable by re-POSTing
 * with ``?confirm=true``), not hard blocks. The hidden variants exist so
 * we can warn the user without leaking the existing deal's id.
 */
export type DealConflictDetail =
  | {
      code: "existing_open_deal_visible";
      message: string;
      contact_id: string;
      existing_deal_id: string;
    }
  | {
      code: "existing_open_deal_hidden";
      message: string;
      contact_id: string;
    }
  | {
      code: "existing_closed_deal_visible";
      message: string;
      contact_id: string;
      existing_deal_id: string;
    }
  | {
      code: "existing_closed_deal_hidden";
      message: string;
      contact_id: string;
    }
  | {
      // 422: caller passed contact_id + account_id but the contact
      // doesn't belong to that account. ``contact_actual_account_id``
      // surfaces the contact's real account so the UI can self-correct
      // (e.g. flip the account picker to that one).
      code: "contact_account_mismatch";
      message: string;
      contact_id: string;
      account_id: string;
      contact_actual_account_id: string | null;
    }
  | {
      // 409: the deal is being assigned to a Sales rep who can't see the
      // parent account. Retrying with ``share: true`` grants that rep
      // explicit access to ``account_ids`` as part of the assignment.
      code: "assignee_lacks_account_access";
      message: string;
      assignee_id: string;
      account_ids: string[];
    };

const DEAL_CONFLICT_CODES = new Set([
  "existing_open_deal_visible",
  "existing_open_deal_hidden",
  "existing_closed_deal_visible",
  "existing_closed_deal_hidden",
  "contact_account_mismatch",
  "assignee_lacks_account_access",
]);

export function isDealConflictDetail(d: unknown): d is DealConflictDetail {
  return (
    typeof d === "object" &&
    d !== null &&
    "code" in d &&
    typeof (d as { code: unknown }).code === "string" &&
    DEAL_CONFLICT_CODES.has((d as { code: string }).code)
  );
}

export const dealsApi = {
  list: (
    params: {
      limit?: number | undefined;
      offset?: number | undefined;
      contact_id?: string | undefined;
      open_only?: boolean | undefined;
      mine_only?: boolean | undefined;
      q?: string | undefined;
      stage?: string | undefined;
      status?: string | undefined;
      value?: string | undefined;
      assigned_to_id?: string | undefined;
      account_id?: string | undefined;
      sort?: "recent" | "urgency" | undefined;
    } = {},
  ) => {
    const search = new URLSearchParams();
    if (params.limit !== undefined) search.set("limit", String(params.limit));
    if (params.offset !== undefined) search.set("offset", String(params.offset));
    if (params.contact_id !== undefined) search.set("contact_id", params.contact_id);
    if (params.open_only !== undefined) search.set("open_only", String(params.open_only));
    if (params.mine_only) search.set("mine_only", String(params.mine_only));
    if (params.q) search.set("q", params.q);
    if (params.stage) search.set("stage", params.stage);
    if (params.status) search.set("status", params.status);
    if (params.value) search.set("value", params.value);
    if (params.assigned_to_id) search.set("assigned_to_id", params.assigned_to_id);
    if (params.account_id) search.set("account_id", params.account_id);
    if (params.sort) search.set("sort", params.sort);
    const qs = search.toString();
    return api<DealList>(`/api/deals${qs ? `?${qs}` : ""}`);
  },
  get: (id: string) => api<Deal>(`/api/deals/${id}`),
  /** Create a deal. Pass `confirm: true` to override the v1.2 duplicate
   *  warning when a contact already has an open or closed deal. Pass
   *  `share: true` to grant a Sales assignee access to the parent account
   *  when they can't see it yet (resolves `assignee_lacks_account_access`). */
  create: (body: DealCreate, opts: { confirm?: boolean; share?: boolean } = {}) => {
    const params = new URLSearchParams();
    if (opts.confirm) params.set("confirm", "true");
    if (opts.share) params.set("share", "true");
    const qs = params.toString();
    return api<Deal>(`/api/deals${qs ? `?${qs}` : ""}`, {
      method: "POST",
      body,
    });
  },
  /** Patch a deal. Pass `share: true` to grant a Sales assignee access to
   *  the parent account when reassigning to someone who can't see it yet
   *  (resolves `assignee_lacks_account_access`). */
  update: (id: string, body: DealUpdate, opts: { share?: boolean } = {}) =>
    api<Deal>(`/api/deals/${id}${opts.share ? "?share=true" : ""}`, { method: "PATCH", body }),
  remove: (id: string) => api<undefined>(`/api/deals/${id}`, { method: "DELETE" }),
  restore: (id: string) => api<Deal>(`/api/deals/${id}/restore`, { method: "POST" }),
  listTrash: (params: { limit?: number; offset?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.limit !== undefined) search.set("limit", String(params.limit));
    if (params.offset !== undefined) search.set("offset", String(params.offset));
    const qs = search.toString();
    return api<TrashedDealList>(`/api/deals/trash/list${qs ? `?${qs}` : ""}`);
  },
};

// ── Follow-ups (PRD §10.3, §11.2, §9.6 + v1.2 §B) ───────────────────────────
//
// As of v1.2 follow-ups are *polymorphic* (parent_type ∈ {contact, account,
// deal}), *multi-assignee* (per-assignee completion state), and *recurring*
// (slim RRULE-style frequency + interval + optional until). The frontend
// types mirror the backend Pydantic schema in `app/schemas/followup.py`.

export type ParentType = "contact" | "account" | "deal";
/**
 * Polymorphic key on a follow-up: real parent (contact/account/deal)
 * OR ``"none"`` for a standalone follow-up (PRD addendum, Apr-2026).
 * When ``"none"``, the follow-up's ``parent_id`` is null.
 */
export type FollowUpParentType = ParentType | "none";

export const RECURRENCE_FREQUENCIES = ["daily", "weekly", "monthly", "yearly"] as const;
export type RecurrenceFrequency = (typeof RECURRENCE_FREQUENCIES)[number];

export interface FollowUpAssigneeRead {
  user_id: string;
  completed_at: string | null;
  completed_by_id: string | null;
}

/**
 * Display-friendly view of a follow-up's parent — populated only on
 * list endpoints (single-item endpoints leave it null because the
 * caller already has the parent context).
 *
 * For deal-parented follow-ups the server resolves through to the
 * deal's *parent* contact or account, so the UI can show the human
 * name and deep-link to that entity rather than to the deal id.
 *
 * ``href_kind`` is what to deep-link to (`/contacts/<id>` or
 * `/accounts/<id>`). The original `parent_id` on the FollowUp is left
 * untouched if a surface still needs the underlying deal/contact id.
 */
export interface FollowUpParentSummary {
  name: string;
  href_kind: "contact" | "account";
  href_id: string;
}

export interface FollowUp {
  id: string;
  /** ``"none"`` for standalone (no parent). */
  parent_type: FollowUpParentType;
  /** Null when ``parent_type === "none"``. */
  parent_id: string | null;
  scheduled_at: string;
  /** Short calendar-card title (PRD §9.6). Null on legacy rows. */
  title: string | null;
  note: string | null;
  is_completed: boolean;
  completed_at: string | null;
  completed_by_id: string | null;
  /** Mandatory closing summary captured at close time (PRD addendum
   *  Apr-2026). Null on still-open rows and on legacy rows that were
   *  closed before the field existed. */
  completion_summary: string | null;
  created_by_id: string | null;
  created_at: string;
  updated_at: string;

  assignees: FollowUpAssigneeRead[];

  recurrence_frequency: RecurrenceFrequency | null;
  recurrence_interval: number | null;
  recurrence_until: string | null;
  parent_followup_id: string | null;

  is_overdue: boolean;

  parent_summary: FollowUpParentSummary | null;
}

export interface FollowUpList {
  items: FollowUp[];
  total: number;
  limit: number;
  offset: number;
}

export interface FollowUpRecurrence {
  frequency: RecurrenceFrequency;
  interval: number;
  until?: string | null;
}

export interface FollowUpCreate {
  /** ``"none"`` for a standalone follow-up — omit ``parent_id`` then. */
  parent_type: FollowUpParentType;
  parent_id?: string | null;
  scheduled_at: string;
  /** Short calendar-card title (PRD §9.6). Strongly recommended for
   *  standalone follow-ups; optional otherwise. */
  title?: string | null;
  note?: string | null;
  /** Omit ⇒ default to [creator]. Pass an empty list to create as
   * "team queue" (intentional). */
  assignee_ids?: string[];
  recurrence?: FollowUpRecurrence | null;
}

export interface FollowUpUpdate {
  scheduled_at?: string;
  title?: string | null;
  note?: string | null;
  assignee_ids?: string[];
  /** Pass `null` to clear recurrence on a series. */
  recurrence?: FollowUpRecurrence | null;
  /** Re-parenting: send BOTH together (or `parent_type: "none"` alone
   *  with `parent_id: null`). The server enforces the lockstep. */
  parent_type?: FollowUpParentType;
  parent_id?: string | null;
}

export interface TrashedFollowUp extends FollowUp {
  deleted_at: string;
  deleted_by_id: string | null;
}

export interface TrashedFollowUpList {
  items: TrashedFollowUp[];
  total: number;
  limit: number;
  offset: number;
}

export const followupsApi = {
  list: (
    params: {
      limit?: number;
      offset?: number;
      /** Pass ``"none"`` to filter to standalone follow-ups. */
      parent_type?: FollowUpParentType;
      parent_id?: string;
      open_only?: boolean;
      mine_only?: boolean;
      assignee_id?: string;
    } = {},
  ) => {
    const search = new URLSearchParams();
    if (params.limit !== undefined) search.set("limit", String(params.limit));
    if (params.offset !== undefined) search.set("offset", String(params.offset));
    if (params.parent_type !== undefined) search.set("parent_type", params.parent_type);
    if (params.parent_id !== undefined) search.set("parent_id", params.parent_id);
    if (params.open_only !== undefined) search.set("open_only", String(params.open_only));
    if (params.mine_only !== undefined) search.set("mine_only", String(params.mine_only));
    if (params.assignee_id !== undefined) search.set("assignee_id", params.assignee_id);
    const qs = search.toString();
    return api<FollowUpList>(`/api/followups${qs ? `?${qs}` : ""}`);
  },
  /**
   * Active users who can be assigned a follow-up on this parent. Pass
   * ``parent_type: "none"`` (and omit ``parent_id``) for standalone.
   */
  eligibleAssignees: (params: { parent_type: FollowUpParentType; parent_id?: string }) => {
    const search = new URLSearchParams();
    search.set("parent_type", params.parent_type);
    if (params.parent_id !== undefined) search.set("parent_id", params.parent_id);
    return api<UserListResponse>(`/api/followups/eligible-assignees?${search.toString()}`);
  },
  get: (id: string) => api<FollowUp>(`/api/followups/${id}`),
  create: (body: FollowUpCreate) => api<FollowUp>("/api/followups", { method: "POST", body }),
  update: (id: string, body: FollowUpUpdate) =>
    api<FollowUp>(`/api/followups/${id}`, { method: "PATCH", body }),
  /**
   * Close the follow-up for the whole team in one decisive move (PRD
   * addendum Apr-2026). Any assignee — or Manager+ — calling this
   * stamps every still-open slice with their id and flips
   * ``is_completed`` true. ``completion_summary`` is mandatory; the
   * server 422s an empty / whitespace-only payload.
   *
   * Idempotent on a follow-up that's already closed: the original
   * summary is preserved (no quiet rewrite on accidental double-click).
   */
  complete: (id: string, body: { completion_summary: string }) =>
    api<FollowUp>(`/api/followups/${id}/complete`, { method: "POST", body }),
  /** Reopen the follow-up. Clears ``completion_summary`` so a re-close
   *  prompts again. The original auto-posted chatter note is left
   *  in place — it's a record of what happened. */
  uncomplete: (id: string) => api<FollowUp>(`/api/followups/${id}/uncomplete`, { method: "POST" }),
  remove: (id: string) => api<undefined>(`/api/followups/${id}`, { method: "DELETE" }),
  restore: (id: string) => api<FollowUp>(`/api/followups/${id}/restore`, { method: "POST" }),
  listTrash: (params: { limit?: number; offset?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.limit !== undefined) search.set("limit", String(params.limit));
    if (params.offset !== undefined) search.set("offset", String(params.offset));
    const qs = search.toString();
    return api<TrashedFollowUpList>(`/api/followups/trash/list${qs ? `?${qs}` : ""}`);
  },
};

// ── Notes (chatter) — PRD v1.2 amendment §C ─────────────────────────────────

export type MentionType = "user" | "contact" | "account" | "deal";

export interface MentionResolved {
  mention_type: MentionType;
  mention_id: string;
  handle?: string | null;
  label?: string | null;
}

/**
 * Note kind discriminator (PRD addendum Apr-2026).
 *
 * * ``user`` — written by a real human via the chatter UI.
 * * ``system`` — server-posted activity entry (e.g. follow-up close).
 *   Rendered with a subtle styling and no edit / delete affordances;
 *   the backend rejects PATCH/DELETE on system rows from non-admins.
 */
export type NoteKind = "user" | "system";

export interface Note {
  id: string;
  parent_type: ParentType;
  parent_id: string;
  /** Defaults to ``user`` on responses from servers older than the
   *  Apr-2026 chatter overhaul. */
  kind: NoteKind;
  body: string;
  edited_at: string | null;
  author_id: string | null;
  /** Server-resolved display name. Null when author was hard-deleted
   * (FK ``ON DELETE SET NULL``) or unset on the user row. */
  author_name: string | null;
  /** Server-resolved email — used as a fallback display label when
   * ``author_name`` is unset. */
  author_email: string | null;
  created_at: string;
  updated_at: string;
  mentions: MentionResolved[];
}

export interface NoteList {
  items: Note[];
  total: number;
  limit: number;
  offset: number;
}

export const notesApi = {
  list: (
    parent_type: ParentType,
    parent_id: string,
    params: { limit?: number; offset?: number } = {},
  ) => {
    const search = new URLSearchParams();
    if (params.limit !== undefined) search.set("limit", String(params.limit));
    if (params.offset !== undefined) search.set("offset", String(params.offset));
    const qs = search.toString();
    return api<NoteList>(`/api/notes/${parent_type}/${parent_id}${qs ? `?${qs}` : ""}`);
  },
  create: (parent_type: ParentType, parent_id: string, body: string) =>
    api<Note>(`/api/notes/${parent_type}/${parent_id}`, { method: "POST", body: { body } }),
  update: (note_id: string, body: string) =>
    api<Note>(`/api/notes/${note_id}`, { method: "PATCH", body: { body } }),
  remove: (note_id: string) => api<undefined>(`/api/notes/${note_id}`, { method: "DELETE" }),
};

// ── Mentions search (autocomplete) — PRD v1.2 amendment §C ──────────────────

export interface MentionCandidate {
  mention_type: MentionType;
  id: string;
  label: string;
  handle: string;
  secondary?: string | null;
}

export interface MentionSearchResponse {
  items: MentionCandidate[];
}

export const mentionsApi = {
  search: (params: {
    q: string;
    types?: MentionType[];
    limit?: number;
    signal?: AbortSignal;
  }) => {
    const search = new URLSearchParams({ q: params.q });
    if (params.types && params.types.length > 0) search.set("types", params.types.join(","));
    if (params.limit !== undefined) search.set("limit", String(params.limit));
    const opts: RequestOpts = params.signal ? { signal: params.signal } : {};
    return api<MentionSearchResponse>(`/api/mentions/search?${search.toString()}`, opts);
  },
};

// ── Notifications (in-app bell) — PRD v1.2 amendment §D ─────────────────────

export const NOTIFICATION_TRIGGER_TYPES = [
  "mention",
  "followup_assigned",
  "followup_reminder",
] as const;
export type NotificationTriggerType = (typeof NOTIFICATION_TRIGGER_TYPES)[number];

export interface Notification {
  id: string;
  trigger_type: NotificationTriggerType;
  trigger_id: string;
  summary: string;
  target_url: string | null;
  in_app_seen_at: string | null;
  created_at: string;
}

export interface NotificationList {
  items: Notification[];
  total: number;
  unread_total: number;
  limit: number;
  offset: number;
}

export interface UnreadCount {
  unread: number;
}

export const notificationsApi = {
  list: (params: { limit?: number; offset?: number; unread_only?: boolean } = {}) => {
    const search = new URLSearchParams();
    if (params.limit !== undefined) search.set("limit", String(params.limit));
    if (params.offset !== undefined) search.set("offset", String(params.offset));
    if (params.unread_only !== undefined) search.set("unread_only", String(params.unread_only));
    const qs = search.toString();
    return api<NotificationList>(`/api/notifications${qs ? `?${qs}` : ""}`);
  },
  unreadCount: () => api<UnreadCount>("/api/notifications/unread-count"),
  /**
   * Mark notifications read. Empty `ids` = "mark all of mine read".
   * Returns 204; we resolve to undefined.
   */
  markRead: (ids: string[] = []) =>
    api<undefined>("/api/notifications/mark-read", {
      method: "POST",
      body: { ids },
    }),
};

// ── Dashboard (PRD §11.2) ───────────────────────────────────────────────────

/**
 * One row in the "urgent deals" widget.
 *
 * Both ``contact_*`` and ``account_name`` may be populated when the
 * deal carries both parents (PRD §8.1 v1.2 — the "this person at
 * this company" common case). UI rule: contact wins for the primary
 * label and the account renders as a secondary chip; if only one is
 * set, that one is the label.
 */
export interface UrgentDealItem {
  deal: Deal;
  contact_name: string | null;
  contact_phone: string | null;
  account_name: string | null;
  via_overdue_followup: boolean;
}

/**
 * Row used by both today- and overdue-followup widgets. Same
 * parent-rendering rules as ``UrgentDealItem``.
 */
export interface UpcomingFollowUpItem {
  followup: FollowUp;
  deal_id: string;
  contact_id: string | null;
  contact_name: string | null;
  account_id: string | null;
  account_name: string | null;
}

export interface DashboardResponse {
  urgent_deals: UrgentDealItem[];
  urgent_deals_total: number;
  today_followups: UpcomingFollowUpItem[];
  today_followups_total: number;
  overdue_followups: UpcomingFollowUpItem[];
  overdue_followups_total: number;
}

export const dashboardApi = {
  /**
   * Returns all three home-page widgets in one round-trip. Pass the
   * caller's UTC-offset-in-minutes (e.g. India = 330) so "today" gets
   * computed in the user's local time, not UTC.
   */
  get: (params: { tz_offset_minutes?: number; per_widget_limit?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.tz_offset_minutes !== undefined)
      search.set("tz_offset_minutes", String(params.tz_offset_minutes));
    if (params.per_widget_limit !== undefined)
      search.set("per_widget_limit", String(params.per_widget_limit));
    const qs = search.toString();
    return api<DashboardResponse>(`/api/dashboard${qs ? `?${qs}` : ""}`);
  },
};

// ── Tags (PRD §14, Apr-2026 addendum) ───────────────────────────────────────

export interface Tag {
  id: string;
  name: string;
  created_by_id: string | null;
  created_at: string;
  updated_at: string;
  /** Populated on list endpoints when ``with_counts=true`` (default). */
  contact_count: number | null;
  account_count: number | null;
}

export interface TagList {
  items: Tag[];
  total: number;
}

/** Returned in the body of a 409 from POST/PATCH /tags so the UI can
 *  switch to the existing tag instead of failing the user's flow. */
export interface TagNameClashDetail {
  code: "tag_name_exists";
  message: string;
  existing: Tag;
}

export function isTagNameClashDetail(d: unknown): d is TagNameClashDetail {
  return (
    typeof d === "object" &&
    d !== null &&
    "code" in d &&
    (d as { code: unknown }).code === "tag_name_exists"
  );
}

export const tagsApi = {
  list: (params: { q?: string; with_counts?: boolean } = {}) => {
    const search = new URLSearchParams();
    if (params.q) search.set("q", params.q);
    if (params.with_counts !== undefined) search.set("with_counts", String(params.with_counts));
    const qs = search.toString();
    return api<TagList>(`/api/tags${qs ? `?${qs}` : ""}`);
  },
  create: (name: string) => api<Tag>("/api/tags", { method: "POST", body: { name } }),
  rename: (id: string, name: string) =>
    api<Tag>(`/api/tags/${id}`, { method: "PATCH", body: { name } }),
  remove: (id: string) => api<undefined>(`/api/tags/${id}`, { method: "DELETE" }),

  // Per-entity attach
  listForContact: (contactId: string) => api<Tag[]>(`/api/contacts/${contactId}/tags`),
  setForContact: (contactId: string, tag_ids: string[]) =>
    api<Tag[]>(`/api/contacts/${contactId}/tags`, {
      method: "PUT",
      body: { tag_ids },
    }),
  listForAccount: (accountId: string) => api<Tag[]>(`/api/accounts/${accountId}/tags`),
  setForAccount: (accountId: string, tag_ids: string[]) =>
    api<Tag[]>(`/api/accounts/${accountId}/tags`, {
      method: "PUT",
      body: { tag_ids },
    }),
};

// ── Picklists (Apr-2026 picklist addendum) ──────────────────────────────────

/**
 * Picklist kinds supported by the API. Mirrors ``PicklistKind`` on the
 * backend; the URL path goes through ``/api/picklists/{kind}``.
 */
export const PICKLIST_KINDS = [
  "deal_stage",
  "deal_status",
  "deal_value",
  "unit_of_measure",
] as const;
export type PicklistKind = (typeof PICKLIST_KINDS)[number];

export const PICKLIST_KIND_LABELS: Record<PicklistKind, string> = {
  deal_stage: "Deal stage",
  deal_status: "Deal status",
  deal_value: "Deal value",
  unit_of_measure: "Unit of measure",
};

/**
 * One picklist option as returned by the API. ``is_locked`` rows are
 * seed values the close / reopen routers depend on — the UI renders
 * them as read-only chips. ``is_terminal`` rows close a deal when set
 * (only ever true on locked rows).
 */
export interface PicklistOption {
  id: string;
  kind: PicklistKind;
  value: string;
  label: string;
  category: string | null;
  position: number;
  is_locked: boolean;
  is_terminal: boolean;
  is_active: boolean;
}

export interface PicklistOptionList {
  items: PicklistOption[];
  total: number;
}

export interface PicklistOptionCreate {
  value: string;
  label: string;
  category?: string | null;
  position?: number | null;
}

export interface PicklistOptionUpdate {
  label?: string;
  category?: string | null;
  position?: number;
  is_active?: boolean;
}

export const picklistsApi = {
  list: (kind: PicklistKind) => api<PicklistOptionList>(`/api/picklists/${kind}`),
  create: (kind: PicklistKind, body: PicklistOptionCreate) =>
    api<PicklistOption>(`/api/picklists/${kind}`, {
      method: "POST",
      body,
    }),
  update: (kind: PicklistKind, id: string, body: PicklistOptionUpdate) =>
    api<PicklistOption>(`/api/picklists/${kind}/${id}`, {
      method: "PATCH",
      body,
    }),
  remove: (kind: PicklistKind, id: string) =>
    api<undefined>(`/api/picklists/${kind}/${id}`, { method: "DELETE" }),
};

// ── ERP — catalog (Apr-2026 ERP v1 PR-A) ────────────────────────────────────
//
// ERP runs alongside the CRM in the same app shell — switched between
// via the app launcher in the topbar. Two-table catalog: Products
// (sellable, manufacturable) and RawMaterials (consumable inputs).
// A Product may carry ``linked_raw_material_id`` to mirror name / UoM
// / is_active onto a sibling RawMaterial row (the 'intermediate
// product' case — a thing manufactured AND consumed by other BOMs).

export interface ErpUoMOption {
  /** Canonical lower-cased code — the value stored on items. */
  code: string;
  /** Human-readable label for the dropdown. */
  label: string;
  /** Group label for the dropdown ("mass" / "volume" / "count" / ...). */
  category: string;
}

export interface ErpUoMOptionList {
  items: ErpUoMOption[];
}

export interface ErpWarehouse {
  id: string;
  code: string;
  name: string;
  address: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ErpWarehouseList {
  items: ErpWarehouse[];
  total: number;
}

export interface ErpWarehouseCreate {
  code?: string | null;
  name: string;
  address?: string | null;
}

export interface ErpWarehouseUpdate {
  name?: string;
  address?: string | null;
  is_active?: boolean;
}

export interface ErpProduct {
  id: string;
  code: string;
  name: string;
  category: string | null;
  version: number;
  uom: string;
  /** String-encoded decimal; `null` means 'no default — quote on each line'. */
  default_sell_price: string | null;
  notes: string | null;
  is_active: boolean;
  linked_raw_material_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ErpProductVersion {
  id: string;
  product_id: string;
  version: number;
  name: string;
  category: string | null;
  uom: string;
  default_sell_price: string | null;
  notes: string | null;
  is_active: boolean;
  linked_raw_material_id: string | null;
  changed_by_id: string | null;
  created_at: string;
}

export interface ErpProductVersionList {
  items: ErpProductVersion[];
}

export interface ErpProductList {
  items: ErpProduct[];
  total: number;
}

export interface ErpProductCreate {
  code?: string | null;
  name: string;
  category?: string | null;
  uom: string;
  default_sell_price?: string | null;
  notes?: string | null;
  linked_raw_material_id?: string | null;
}

export interface ErpProductUpdate {
  name?: string;
  category?: string | null;
  uom?: string;
  default_sell_price?: string | null;
  notes?: string | null;
  is_active?: boolean;
  linked_raw_material_id?: string | null;
}

export interface ErpRawMaterial {
  id: string;
  code: string;
  name: string;
  uom: string;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ErpRawMaterialList {
  items: ErpRawMaterial[];
  total: number;
}

export interface ErpRawMaterialCreate {
  code?: string | null;
  name: string;
  uom: string;
  notes?: string | null;
}

export interface ErpRawMaterialUpdate {
  name?: string;
  uom?: string;
  notes?: string | null;
  is_active?: boolean;
}

/**
 * 409 detail for code / linkage clashes on the ERP catalog endpoints.
 * Captured here so the UI can switch error rendering on `code`.
 */
export type ErpCatalogConflict =
  | { code: "warehouse_code_exists"; message: string }
  | { code: "product_code_exists"; message: string }
  | { code: "raw_material_code_exists"; message: string }
  | {
      code: "raw_material_already_linked";
      message: string;
      linked_to_product_id: string;
    }
  | {
      code: "raw_material_mirrored_from_product";
      message: string;
      mirroring_product_id: string;
    }
  | { code: "warehouse_in_use"; message: string }
  | { code: "product_in_use"; message: string }
  | { code: "raw_material_in_use"; message: string };

export function isErpCatalogConflict(d: unknown): d is ErpCatalogConflict {
  if (typeof d !== "object" || d === null || !("code" in d)) return false;
  const c = (d as { code: unknown }).code;
  return (
    c === "warehouse_code_exists" ||
    c === "product_code_exists" ||
    c === "raw_material_code_exists" ||
    c === "raw_material_already_linked" ||
    c === "raw_material_mirrored_from_product" ||
    c === "warehouse_in_use" ||
    c === "product_in_use" ||
    c === "raw_material_in_use"
  );
}

// ───────────────────────────────────────────────────────────────────────────
//   Inventory (PR-B): rows joined with display fields, plus adjustments
// ───────────────────────────────────────────────────────────────────────────

export type ErpInventoryItemKind = "product" | "raw_material";

export interface ErpInventoryRow {
  item_kind: ErpInventoryItemKind;
  item_id: string;
  item_code: string;
  item_name: string;
  item_uom: string;
  item_active: boolean;
  warehouse_id: string;
  warehouse_code: string;
  warehouse_name: string;
  warehouse_active: boolean;
  qty: string;
}

export interface ErpInventoryList {
  items: ErpInventoryRow[];
  total: number;
}

export interface ErpInventoryAdjustment {
  id: string;
  item_kind: ErpInventoryItemKind;
  product_id: string | null;
  raw_material_id: string | null;
  warehouse_id: string | null;
  document_id: string | null;
  item_code: string | null;
  item_name: string | null;
  warehouse_code: string | null;
  warehouse_name: string | null;
  delta: string;
  reason: string;
  created_by_id: string | null;
  created_by_name: string | null;
  created_at: string;
  qty_before: string | null;
  qty_after: string | null;
  reversal_of_id: string | null;
  reversed_by_adjustment_id: string | null;
}

export interface ErpInventoryAdjustmentList {
  items: ErpInventoryAdjustment[];
  total: number;
}

export interface ErpInventoryAdjustmentEvent {
  id: string;
  event_type: "document" | "manual";
  status: "active" | "reversed" | "reversal";
  document_id: string | null;
  document_type: ErpInventoryDocumentType | null;
  movement_type: ErpInventoryDocumentDirection | null;
  document_number: string | null;
  document_date: string | null;
  original_filename: string | null;
  warehouse_id: string | null;
  warehouse_code: string | null;
  warehouse_name: string | null;
  destination_warehouse_id: string | null;
  destination_warehouse_code: string | null;
  destination_warehouse_name: string | null;
  reason: string;
  created_by_id: string | null;
  created_by_name: string | null;
  created_at: string;
  lines: ErpInventoryAdjustment[];
}

export interface ErpInventoryAdjustmentEventList {
  items: ErpInventoryAdjustmentEvent[];
  total: number;
}

export interface ErpInventoryAdjustmentCreate {
  item_kind: ErpInventoryItemKind;
  product_id?: string;
  raw_material_id?: string;
  warehouse_id: string;
  /** Decimal as a string ("3.5", "-2"). */
  delta: string;
  reason: string;
  /** Manager-only override that allows pushing further-negative on
   *  rows already at qty < 0. Server silently ignores for non-managers. */
  allow_negative?: boolean;
}

export interface ErpInventoryAdjustmentApplied {
  adjustment: ErpInventoryAdjustment;
  qty_before: string;
  qty_after: string;
  warned: boolean;
}

export type ErpInventoryDocumentType = "delivery_challan" | "invoice" | "sales_return";
export type ErpInventoryDocumentDirection = "add" | "subtract" | "transfer";

export interface ErpInventoryDocumentLineParsed {
  source_sku: string;
  product_id: string | null;
  product_code: string | null;
  product_name: string | null;
  description: string;
  hsn: string;
  quantity: string;
  uom: string;
  match_type: "exact" | "prefix_alias" | "unmatched";
  current_qty: string | null;
  resulting_qty: string | null;
  destination_current_qty: string | null;
  destination_resulting_qty: string | null;
}

export interface ErpInventoryDocumentParsed {
  document_type: ErpInventoryDocumentType;
  direction: ErpInventoryDocumentDirection;
  document_number: string;
  document_date: string | null;
  original_invoice_number: string | null;
  original_filename: string;
  file_hash: string;
  warehouse_id: string;
  destination_warehouse_id: string | null;
  parse_token: string;
  lines: ErpInventoryDocumentLineParsed[];
}

export interface ErpInventoryDocumentLineApply {
  source_sku: string;
  product_id: string;
  description: string;
  quantity: string;
  uom: string;
}

export interface ErpInventoryDocumentApplied {
  document_id: string;
  document_type: ErpInventoryDocumentType;
  document_number: string;
  warehouse_id: string;
  destination_warehouse_id: string | null;
  lines: {
    product_id: string;
    product_code: string;
    quantity: string;
    delta: string;
    qty_before: string;
    qty_after: string;
    destination_qty_before: string | null;
    destination_qty_after: string | null;
  }[];
}

export async function parseInventoryDocument(
  warehouseId: string,
  documentType: ErpInventoryDocumentType,
  direction: ErpInventoryDocumentDirection,
  file: File,
  destinationWarehouseId?: string,
): Promise<ErpInventoryDocumentParsed> {
  const form = new FormData();
  form.append("warehouse_id", warehouseId);
  form.append("document_type", documentType);
  form.append("direction", direction);
  if (destinationWarehouseId) {
    form.append("destination_warehouse_id", destinationWarehouseId);
  }
  form.append("file", file);
  const res = await fetch("/api/erp/inventory/documents/parse", {
    method: "POST",
    body: form,
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  const parsed = (await res.json()) as unknown;
  if (!res.ok) {
    const detail =
      parsed && typeof parsed === "object" && "detail" in parsed
        ? (parsed as { detail: unknown }).detail
        : parsed;
    throw new ApiError(res.status, humanizeErrorDetail(detail, res), detail);
  }
  return parsed as ErpInventoryDocumentParsed;
}

export async function parseInventoryDocumentAmendment(
  documentId: string,
  file: File,
): Promise<ErpInventoryDocumentParsed> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`/api/erp/inventory/documents/${documentId}/amendments/parse`, {
    method: "POST",
    body: form,
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  const parsed = (await res.json()) as unknown;
  if (!res.ok) {
    const detail =
      parsed && typeof parsed === "object" && "detail" in parsed
        ? (parsed as { detail: unknown }).detail
        : parsed;
    throw new ApiError(res.status, humanizeErrorDetail(detail, res), detail);
  }
  return parsed as ErpInventoryDocumentParsed;
}

/** 409 detail for the negative-stock guard. */
export interface ErpInventoryNegativeBlocked {
  code: "inventory_negative_blocked";
  message: string;
  current_qty: string;
  attempted_delta: string;
}

export function isInventoryNegativeBlocked(d: unknown): d is ErpInventoryNegativeBlocked {
  return (
    typeof d === "object" &&
    d !== null &&
    "code" in d &&
    (d as { code: unknown }).code === "inventory_negative_blocked"
  );
}

export interface ErpInventoryListFilters {
  warehouse_id?: string;
  item_kind?: ErpInventoryItemKind;
  product_id?: string;
  raw_material_id?: string;
  include_zero?: boolean;
}

export interface ErpInventoryAdjustmentListFilters {
  warehouse_id?: string;
  product_id?: string;
  raw_material_id?: string;
  limit?: number;
  offset?: number;
}

// ── Manufacturing (PR-D) ──────────────────────────────────────────────────

export type ErpMfgJobStatus = "planned" | "started" | "completed" | "cancelled";

export interface ErpMfgJob {
  id: string;
  code: string;
  product_id: string;
  product_code: string;
  product_name: string;
  product_uom: string;
  qty: string;
  warehouse_id: string;
  warehouse_code: string;
  warehouse_name: string;
  status: ErpMfgJobStatus;
  notes: string | null;
  started_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  created_by_id: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface ErpMfgJobList {
  items: ErpMfgJob[];
  total: number;
}

export interface ErpMfgJobCreate {
  code?: string;
  product_id: string;
  qty: string;
  warehouse_id: string;
  notes?: string;
}

export interface ErpMfgJobUpdate {
  qty?: string;
  warehouse_id?: string;
  notes?: string | null;
}

export interface ErpMfgJobCancel {
  reason?: string;
}

export interface ErpMfgListFilters {
  status?: ErpMfgJobStatus;
}

export type ErpMfgConflict =
  | { code: "manufacturing_job_not_startable"; message: string }
  | { code: "manufacturing_job_not_completable"; message: string }
  | { code: "manufacturing_job_not_cancellable"; message: string }
  | { code: "manufacturing_job_not_editable"; message: string }
  | { code: "manufacturing_job_not_deletable"; message: string }
  | { code: "bom_missing_or_empty"; message: string }
  | {
      code: "inventory_negative_blocked";
      message: string;
      blocked: Array<{
        item_kind: string;
        item_code: string;
        warehouse_code: string;
        qty_before: string;
        delta: string;
      }>;
    }
  | { code: "product_inactive"; message: string }
  | { code: "warehouse_inactive"; message: string };

export function isErpMfgConflict(d: unknown): d is ErpMfgConflict {
  if (typeof d !== "object" || d === null || !("code" in d)) return false;
  const c = (d as { code: unknown }).code;
  return (
    c === "manufacturing_job_not_startable" ||
    c === "manufacturing_job_not_completable" ||
    c === "manufacturing_job_not_cancellable" ||
    c === "manufacturing_job_not_editable" ||
    c === "manufacturing_job_not_deletable" ||
    c === "bom_missing_or_empty" ||
    c === "inventory_negative_blocked" ||
    c === "product_inactive" ||
    c === "warehouse_inactive"
  );
}

// ── Drivers + Transports (PR-E) ────────────────────────────────────────────

export interface ErpDriver {
  id: string;
  code: string;
  name: string;
  phone: string | null;
  license: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ErpDriverList {
  items: ErpDriver[];
  total: number;
}

export interface ErpDriverCreate {
  code?: string;
  name: string;
  phone?: string;
  license?: string;
}

export interface ErpDriverUpdate {
  name?: string;
  phone?: string | null;
  license?: string | null;
  is_active?: boolean;
}

export interface ErpTransport {
  id: string;
  code: string;
  vehicle_number: string;
  type: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ErpTransportList {
  items: ErpTransport[];
  total: number;
}

export interface ErpTransportCreate {
  code?: string;
  vehicle_number: string;
  type?: string;
}

export interface ErpTransportUpdate {
  vehicle_number?: string;
  type?: string | null;
  is_active?: boolean;
}

export type ErpDispatchMasterConflict =
  | { code: "driver_code_exists"; message: string }
  | { code: "driver_in_use"; message: string }
  | { code: "transport_code_exists"; message: string }
  | { code: "transport_in_use"; message: string };

export function isErpDispatchMasterConflict(d: unknown): d is ErpDispatchMasterConflict {
  if (typeof d !== "object" || d === null || !("code" in d)) return false;
  const c = (d as { code: unknown }).code;
  return (
    c === "driver_code_exists" ||
    c === "driver_in_use" ||
    c === "transport_code_exists" ||
    c === "transport_in_use"
  );
}

// ── Dispatches (PR-F) ──────────────────────────────────────────────────────

export type ErpDispatchStatus = "draft" | "confirmed" | "in_transit" | "delivered" | "cancelled";

export type ErpDeliveryKind = "external" | "internal";
export type ErpDeliveryLineItemKind = "product" | "raw_material" | "custom";

export interface ErpDeliveryLineWrite {
  item_kind: ErpDeliveryLineItemKind;
  product_id?: string | null;
  raw_material_id?: string | null;
  custom_name?: string | null;
  custom_uom?: string | null;
  qty: string;
  unit_price?: string | null;
  position?: number;
}

export interface ErpDeliveryLineRead {
  id: string;
  item_kind: ErpDeliveryLineItemKind;
  product_id: string | null;
  raw_material_id: string | null;
  custom_name: string | null;
  item_code: string | null;
  item_name: string | null;
  item_uom: string | null;
  qty: string;
  unit_price: string | null;
  line_total: string | null;
  position: number;
}

export interface ErpDeliveryWrite {
  kind: ErpDeliveryKind;
  source_warehouse_id: string;
  dest_warehouse_id?: string | null;
  account_id?: string | null;
  contact_id?: string | null;
  order_id?: string | null;
  delivery_address?: string | null;
  notes?: string | null;
  position?: number;
  lines?: ErpDeliveryLineWrite[];
}

export interface ErpDeliveryRead {
  id: string;
  kind: ErpDeliveryKind;
  source_warehouse_id: string;
  source_warehouse_code: string;
  source_warehouse_name: string;
  dest_warehouse_id: string | null;
  dest_warehouse_code: string | null;
  dest_warehouse_name: string | null;
  account_id: string | null;
  account_name: string | null;
  contact_id: string | null;
  contact_name: string | null;
  order_id: string | null;
  order_code: string | null;
  delivery_address: string | null;
  resolved_address: string | null;
  notes: string | null;
  position: number;
  lines: ErpDeliveryLineRead[];
  delivery_total: string | null;
}

export interface ErpDispatchCreate {
  code?: string | null;
  driver_id?: string | null;
  transport_id?: string | null;
  temp_driver_name?: string | null;
  temp_transport_name?: string | null;
  deal_id?: string | null;
  account_id?: string | null;
  contact_id?: string | null;
  notes?: string | null;
  deliveries?: ErpDeliveryWrite[];
}

export interface ErpDispatchUpdate {
  driver_id?: string | null;
  transport_id?: string | null;
  temp_driver_name?: string | null;
  temp_transport_name?: string | null;
  deal_id?: string | null;
  account_id?: string | null;
  contact_id?: string | null;
  notes?: string | null;
}

export interface ErpDispatchReplaceDeliveries {
  deliveries: ErpDeliveryWrite[];
}

export interface ErpDispatchCancel {
  reason?: string | null;
}

export interface ErpDispatchRead {
  id: string;
  code: string;
  status: ErpDispatchStatus;
  driver_id: string | null;
  driver_code: string | null;
  driver_name: string | null;
  transport_id: string | null;
  transport_code: string | null;
  transport_vehicle_number: string | null;
  temp_driver_name: string | null;
  temp_transport_name: string | null;
  deal_id: string | null;
  deal_title: string | null;
  account_id: string | null;
  account_name: string | null;
  contact_id: string | null;
  contact_name: string | null;
  notes: string | null;
  confirmed_at: string | null;
  in_transit_at: string | null;
  delivered_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  created_by_id: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  deliveries: ErpDeliveryRead[];
  dispatch_total: string | null;
}

export interface ErpDispatchList {
  items: ErpDispatchRead[];
  total: number;
}

export interface ErpDispatchListFilters {
  status?: ErpDispatchStatus;
  /** Match dispatches with this CRM link at header- or delivery-level. */
  deal_id?: string;
  account_id?: string;
  contact_id?: string;
}

export interface ErpLastSoldHint {
  item_kind: ErpDeliveryLineItemKind;
  product_id: string | null;
  raw_material_id: string | null;
  last_unit_price: string;
  last_sold_at: string;
  dispatch_code: string;
}

// ── Orders (ERP v1.2) ──────────────────────────────────────────────────────

export type ErpOrderStatus =
  | "draft"
  | "pending_production"
  | "ready_for_dispatch"
  | "partially_fulfilled"
  | "completed"
  | "cancelled";

export type ErpOrderLineItemKind = "product" | "raw_material" | "custom";

export interface ErpOrderLineWrite {
  item_kind: ErpOrderLineItemKind;
  product_id?: string | null;
  raw_material_id?: string | null;
  custom_name?: string | null;
  custom_uom?: string | null;
  qty: string;
  unit_price?: string | null;
  is_custom?: boolean;
  position?: number;
}

export interface ErpOrderLineRead {
  id: string;
  item_kind: ErpOrderLineItemKind;
  product_id: string | null;
  raw_material_id: string | null;
  custom_name: string | null;
  item_code: string | null;
  item_name: string | null;
  item_uom: string | null;
  qty: string;
  unit_price: string | null;
  line_total: string | null;
  is_custom: boolean;
  position: number;
  qty_fulfilled: string;
  qty_remaining: string | null;
  on_hand_qty: string | null;
  due_qty: string | null;
}

export interface ErpProductionOrderLineRead {
  id: string;
  item_kind: ErpOrderLineItemKind;
  item_code: string | null;
  item_name: string | null;
  item_uom: string | null;
  qty: string;
  is_custom: boolean;
  position: number;
  qty_fulfilled: string;
  qty_remaining: string | null;
  on_hand_qty: string | null;
  due_qty: string | null;
}

export interface ErpOrderCreate {
  code?: string | null;
  deal_id?: string | null;
  account_id?: string | null;
  contact_id?: string | null;
  notes?: string | null;
  lines?: ErpOrderLineWrite[];
}

export interface ErpOrderUpdate {
  deal_id?: string | null;
  account_id?: string | null;
  contact_id?: string | null;
  notes?: string | null;
}

export interface ErpOrderReplaceLines {
  lines: ErpOrderLineWrite[];
}

export interface ErpOrderCancel {
  reason?: string | null;
}

export interface ErpOrderRead {
  id: string;
  code: string;
  status: ErpOrderStatus;
  deal_id: string | null;
  account_id: string | null;
  account_name: string | null;
  account_address: string | null;
  contact_id: string | null;
  contact_name: string | null;
  contact_address: string | null;
  notes: string | null;
  has_custom: boolean;
  confirmed_at: string | null;
  ready_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  created_by_id: string | null;
  created_at: string;
  updated_at: string;
  lines: ErpOrderLineRead[];
  order_total: string | null;
}

export interface ErpProductionOrderRead {
  id: string;
  code: string;
  status: ErpOrderStatus;
  has_custom: boolean;
  confirmed_at: string | null;
  ready_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
  lines: ErpProductionOrderLineRead[];
}

export type ErpOrderResponse = ErpOrderRead | ErpProductionOrderRead;

export interface ErpOrderListItem {
  id: string;
  code: string;
  status: ErpOrderStatus;
  deal_id: string | null;
  account_id: string | null;
  account_name: string | null;
  contact_id: string | null;
  contact_name: string | null;
  line_count: number;
  has_custom: boolean;
  order_total: string | null;
  created_at: string;
  updated_at: string;
}

export interface ErpOrderList {
  items: ErpOrderListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface ErpOrderListFilters {
  status?: ErpOrderStatus;
  deal_id?: string;
  account_id?: string;
  contact_id?: string;
  q?: string;
  custom?: boolean;
  limit?: number;
  offset?: number;
}

export interface ErpItemAvailability {
  product_id: string;
  on_hand: string;
  booked: string;
  available: string;
}

export interface ErpItemAvailabilityList {
  items: ErpItemAvailability[];
}

/**
 * Validation conflict shapes the dispatch router returns in 409 responses.
 * Codes are documented in `app/routers/erp/dispatches.py`. We expose the
 * union here so callers can pattern-match in the UI.
 */
export type ErpDispatchConflict =
  | { code: "dispatch_code_exists"; message: string }
  | { code: "dispatch_not_found"; message: string }
  | { code: "dispatch_not_editable"; message: string }
  | { code: "dispatch_not_confirmable"; message: string }
  | { code: "dispatch_not_in_transit"; message: string }
  | { code: "dispatch_not_deliverable"; message: string }
  | { code: "dispatch_not_deletable"; message: string }
  | { code: "dispatch_already_cancelled"; message: string }
  | { code: "dispatch_no_deliveries"; message: string }
  | { code: "driver_not_found"; message: string }
  | { code: "driver_inactive"; message: string }
  | { code: "transport_not_found"; message: string }
  | { code: "transport_inactive"; message: string }
  | { code: "deal_not_found"; message: string }
  | { code: "account_not_found"; message: string }
  | { code: "contact_not_found"; message: string }
  | { code: "warehouse_not_found"; message: string }
  | { code: "warehouse_inactive"; message: string }
  | { code: "warehouse_same_source_dest"; message: string }
  | { code: "external_delivery_recipient_required"; message: string }
  | { code: "external_delivery_contact_account_mismatch"; message: string }
  | { code: "external_delivery_dest_forbidden"; message: string }
  | { code: "external_delivery_line_price_required"; message: string }
  | { code: "internal_delivery_dest_required"; message: string }
  | { code: "internal_delivery_recipient_forbidden"; message: string }
  | { code: "internal_delivery_line_price_forbidden"; message: string }
  | { code: "delivery_line_item_required"; message: string }
  | { code: "delivery_line_item_kind_mismatch"; message: string }
  | {
      code: "inventory_negative_blocked";
      message: string;
      blocked: { item_kind: string; item_id: string; warehouse_id: string }[];
    };

export function isErpDispatchConflict(d: unknown): d is ErpDispatchConflict {
  if (typeof d !== "object" || d === null || !("code" in d)) return false;
  const c = (d as { code: unknown }).code;
  return typeof c === "string" && c.length > 0;
}

function _qs(params: Record<string, string | number | boolean | undefined>): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    sp.set(key, String(value));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

function _filtersToRecord(obj: object): Record<string, string | number | boolean | undefined> {
  return obj as Record<string, string | number | boolean | undefined>;
}

export const erpApi = {
  uoms: () => api<ErpUoMOptionList>("/api/erp/uoms"),

  // Warehouses
  warehouses: {
    list: () => api<ErpWarehouseList>("/api/erp/warehouses"),
    get: (id: string) => api<ErpWarehouse>(`/api/erp/warehouses/${id}`),
    create: (body: ErpWarehouseCreate) =>
      api<ErpWarehouse>("/api/erp/warehouses", { method: "POST", body }),
    update: (id: string, body: ErpWarehouseUpdate) =>
      api<ErpWarehouse>(`/api/erp/warehouses/${id}`, { method: "PATCH", body }),
    remove: (id: string) => api<undefined>(`/api/erp/warehouses/${id}`, { method: "DELETE" }),
  },

  // Products
  products: {
    list: () => api<ErpProductList>("/api/erp/products"),
    get: (id: string) => api<ErpProduct>(`/api/erp/products/${id}`),
    create: (body: ErpProductCreate) =>
      api<ErpProduct>("/api/erp/products", { method: "POST", body }),
    update: (id: string, body: ErpProductUpdate) =>
      api<ErpProduct>(`/api/erp/products/${id}`, { method: "PATCH", body }),
    versions: (id: string) =>
      api<ErpProductVersionList>(`/api/erp/products/${id}/versions`),
    remove: (id: string) => api<undefined>(`/api/erp/products/${id}`, { method: "DELETE" }),
  },

  // Raw materials
  rawMaterials: {
    list: () => api<ErpRawMaterialList>("/api/erp/raw-materials"),
    get: (id: string) => api<ErpRawMaterial>(`/api/erp/raw-materials/${id}`),
    create: (body: ErpRawMaterialCreate) =>
      api<ErpRawMaterial>("/api/erp/raw-materials", { method: "POST", body }),
    update: (id: string, body: ErpRawMaterialUpdate) =>
      api<ErpRawMaterial>(`/api/erp/raw-materials/${id}`, {
        method: "PATCH",
        body,
      }),
    remove: (id: string) => api<undefined>(`/api/erp/raw-materials/${id}`, { method: "DELETE" }),
  },

  // Inventory (read for any auth user; mutation manager-only on the server)
  inventory: {
    list: (filters: ErpInventoryListFilters = {}) =>
      api<ErpInventoryList>(`/api/erp/inventory${_qs(_filtersToRecord(filters))}`),
    listAdjustments: (filters: ErpInventoryAdjustmentListFilters = {}) =>
      api<ErpInventoryAdjustmentList>(
        `/api/erp/inventory/adjustments${_qs(_filtersToRecord(filters))}`,
      ),
    listAdjustmentEvents: (filters: ErpInventoryAdjustmentListFilters = {}) =>
      api<ErpInventoryAdjustmentEventList>(
        `/api/erp/inventory/adjustment-events${_qs(_filtersToRecord(filters))}`,
      ),
    createAdjustment: (body: ErpInventoryAdjustmentCreate) =>
      api<ErpInventoryAdjustmentApplied>("/api/erp/inventory/adjustments", {
        method: "POST",
        body,
      }),
    reverseAdjustment: (id: string, body: { reason: string; allow_negative?: boolean }) =>
      api<ErpInventoryAdjustmentApplied>(`/api/erp/inventory/adjustments/${id}/reverse`, {
        method: "POST",
        body,
      }),
    reverseDocument: (id: string, body: { reason: string; allow_negative?: boolean }) =>
      api<ErpInventoryAdjustmentApplied[]>(`/api/erp/inventory/documents/${id}/reverse`, {
        method: "POST",
        body,
      }),
    applyDocument: (body: {
      parse_token: string;
      lines: ErpInventoryDocumentLineApply[];
    }) =>
      api<ErpInventoryDocumentApplied>("/api/erp/inventory/documents/apply", {
        method: "POST",
        body,
      }),
    applyDocumentAmendment: (
      documentId: string,
      body: {
        parse_token: string;
        reason: string;
        lines: ErpInventoryDocumentLineApply[];
      },
    ) =>
      api<ErpInventoryDocumentApplied>(
        `/api/erp/inventory/documents/${documentId}/amendments`,
        { method: "POST", body },
      ),
  },

  // Manufacturing (manager-only on the server for all routes)
  manufacturing: {
    list: (filters: ErpMfgListFilters = {}) =>
      api<ErpMfgJobList>(`/api/erp/manufacturing-jobs${_qs(_filtersToRecord(filters))}`),
    get: (id: string) => api<ErpMfgJob>(`/api/erp/manufacturing-jobs/${id}`),
    create: (body: ErpMfgJobCreate) =>
      api<ErpMfgJob>("/api/erp/manufacturing-jobs", { method: "POST", body }),
    update: (id: string, body: ErpMfgJobUpdate) =>
      api<ErpMfgJob>(`/api/erp/manufacturing-jobs/${id}`, { method: "PATCH", body }),
    remove: (id: string) =>
      api<undefined>(`/api/erp/manufacturing-jobs/${id}`, { method: "DELETE" }),
    start: (id: string) =>
      api<ErpMfgJob>(`/api/erp/manufacturing-jobs/${id}/start`, { method: "POST" }),
    complete: (id: string) =>
      api<ErpMfgJob>(`/api/erp/manufacturing-jobs/${id}/complete`, { method: "POST" }),
    cancel: (id: string, body?: ErpMfgJobCancel) =>
      api<ErpMfgJob>(`/api/erp/manufacturing-jobs/${id}/cancel`, {
        method: "POST",
        body: body ?? {},
      }),
  },

  // Drivers + Transports (read for any auth user; mutation manager-only)
  drivers: {
    list: () => api<ErpDriverList>("/api/erp/drivers"),
    get: (id: string) => api<ErpDriver>(`/api/erp/drivers/${id}`),
    create: (body: ErpDriverCreate) => api<ErpDriver>("/api/erp/drivers", { method: "POST", body }),
    update: (id: string, body: ErpDriverUpdate) =>
      api<ErpDriver>(`/api/erp/drivers/${id}`, { method: "PATCH", body }),
    remove: (id: string) => api<undefined>(`/api/erp/drivers/${id}`, { method: "DELETE" }),
  },

  transports: {
    list: () => api<ErpTransportList>("/api/erp/transports"),
    get: (id: string) => api<ErpTransport>(`/api/erp/transports/${id}`),
    create: (body: ErpTransportCreate) =>
      api<ErpTransport>("/api/erp/transports", { method: "POST", body }),
    update: (id: string, body: ErpTransportUpdate) =>
      api<ErpTransport>(`/api/erp/transports/${id}`, { method: "PATCH", body }),
    remove: (id: string) => api<undefined>(`/api/erp/transports/${id}`, { method: "DELETE" }),
  },

  // Dispatches (PR-F: manager/admin only at the router layer).
  dispatches: {
    list: (filters: ErpDispatchListFilters = {}) =>
      api<ErpDispatchList>(`/api/erp/dispatches${_qs(_filtersToRecord(filters))}`),
    get: (id: string) => api<ErpDispatchRead>(`/api/erp/dispatches/${id}`),
    create: (body: ErpDispatchCreate) =>
      api<ErpDispatchRead>("/api/erp/dispatches", { method: "POST", body }),
    update: (id: string, body: ErpDispatchUpdate) =>
      api<ErpDispatchRead>(`/api/erp/dispatches/${id}`, { method: "PATCH", body }),
    replaceDeliveries: (id: string, body: ErpDispatchReplaceDeliveries) =>
      api<ErpDispatchRead>(`/api/erp/dispatches/${id}/deliveries`, {
        method: "PUT",
        body,
      }),
    remove: (id: string) => api<undefined>(`/api/erp/dispatches/${id}`, { method: "DELETE" }),
    confirm: (id: string) =>
      api<ErpDispatchRead>(`/api/erp/dispatches/${id}/confirm`, { method: "POST" }),
    inTransit: (id: string) =>
      api<ErpDispatchRead>(`/api/erp/dispatches/${id}/in-transit`, { method: "POST" }),
    deliver: (id: string) =>
      api<ErpDispatchRead>(`/api/erp/dispatches/${id}/deliver`, { method: "POST" }),
    cancel: (id: string, body?: ErpDispatchCancel) =>
      api<ErpDispatchRead>(`/api/erp/dispatches/${id}/cancel`, {
        method: "POST",
        body: body ?? {},
      }),
    lastSold: (accountId: string) =>
      api<ErpLastSoldHint[]>(
        `/api/erp/dispatches/last-sold/lookup${_qs({ account_id: accountId })}`,
      ),
  },
  orders: {
    list: (filters: ErpOrderListFilters = {}) =>
      api<ErpOrderList>(`/api/erp/orders${_qs(_filtersToRecord(filters))}`),
    get: (id: string) => api<ErpOrderRead>(`/api/erp/orders/${id}`),
    create: (body: ErpOrderCreate) =>
      api<ErpOrderRead>("/api/erp/orders", { method: "POST", body }),
    update: (id: string, body: ErpOrderUpdate) =>
      api<ErpOrderRead>(`/api/erp/orders/${id}`, { method: "PATCH", body }),
    replaceLines: (id: string, body: ErpOrderReplaceLines) =>
      api<ErpOrderRead>(`/api/erp/orders/${id}/lines`, { method: "PUT", body }),
    submit: (id: string) => api<ErpOrderRead>(`/api/erp/orders/${id}/submit`, { method: "POST" }),
    markReady: (id: string) =>
      api<ErpOrderRead>(`/api/erp/orders/${id}/mark-ready`, { method: "POST" }),
    complete: (id: string) =>
      api<ErpOrderRead>(`/api/erp/orders/${id}/complete`, { method: "POST" }),
    cancel: (id: string, body?: ErpOrderCancel) =>
      api<ErpOrderRead>(`/api/erp/orders/${id}/cancel`, {
        method: "POST",
        body: body ?? {},
      }),
    remove: (id: string) => api<undefined>(`/api/erp/orders/${id}`, { method: "DELETE" }),
    availability: () => api<ErpItemAvailabilityList>("/api/erp/orders/availability"),
  },
};
